import type { MetricName, RangeResult } from "schemas/generated/analyticsRead";

import * as React from "react";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { calculateDelta } from "@v1/features/analytics/analytics";
import MetricWithDelta from "@v1/features/analytics/MetricWithDelta";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

type MetricsOverviewProps = {
  mainRangeResults: RangeResult[];
  compareAtRangeResults: RangeResult[];
  isLoading: boolean;
  handleMetricChange: (metric: MetricName) => void;
  activeMetric: MetricName;
  comparisonType: string;
  benchmarks: Record<string, number> | undefined;
};

export const MetricsOverview: React.FC<MetricsOverviewProps> = ({
  mainRangeResults,
  compareAtRangeResults,
  isLoading,
  handleMetricChange,
  activeMetric,
  comparisonType,
  benchmarks,
}) => {
  const mainRangeResult = mainRangeResults[0];

  const metricsCards: {
    key: MetricName;
    label: string;
  }[] = [
    {
      key: "revenue",
      label: "Total Revenue",
    },
    {
      key: "conversions",
      label: "Total Purchases",
    },
    {
      key: "unique_sessions",
      label: "Unique Sessions",
    },
    {
      key: "conversion_rates",
      label: "Conversion Rate",
    },
    {
      key: "average_order_value",
      label: "Average Order Value",
    },
    {
      key: "revenue_per_session",
      label: "Revenue Per Session",
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-6 gap-4">
        {metricsCards.map((metricCard) => {
          return <Skeleton key={metricCard.key} className="h-22 w-full" />;
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-6 gap-4">
      {metricsCards.map((metricCard) => {
        const { key, label } = metricCard;

        const mainValues = mainRangeResult?.metrics[key] ?? [];
        const mainValue = mainValues?.[0];

        const compareValue = exhaustiveSwitch({ type: comparisonType })({
          previousPeriod: () => {
            const compareValues = compareAtRangeResults[0]?.metrics[key];
            return compareValues?.[0];
          },
          benchmarks: benchmarks?.[key],
        });
        const doesCompareValueExist = compareValue != undefined;

        const isActive = activeMetric === key;

        return (
          <div
            key={key}
            className={twMerge(
              "border-border flex flex-col gap-1 rounded-lg border px-3 py-2 cursor-pointer hover:bg-sheet",
              isActive && "bg-sheet border-default",
            )}
            onClick={() => handleMetricChange(key)}
          >
            <span className="text-xs">{label}</span>
            <MetricWithDelta
              name={key}
              value={mainValue ?? null}
              delta={calculateDelta(mainValue, compareValue)}
              doesCompareValueExist={doesCompareValueExist}
              wrapperClassName="block space-y-1"
              valueClassName="font-semibold"
              tagClassName={
                comparisonType === "benchmarks"
                  ? "text-indigo-600 bg-indigo-50"
                  : undefined
              }
            />
          </div>
        );
      })}
    </div>
  );
};
